import { BundlePeriod, InsertionStatus, type Insertion } from '@/types';

import { isMultiWeeksInsertion, isWithinFreeWeekPeriod } from './pricingUtils';

const ZURICH_TIMEZONE = 'Europe/Zurich';

/**
 * Calculates the start of the next period.
 *
 * Given a series of periods of length `periodLength` starting from `startDate`, this function
 * calculates the start of the next period after `referenceDate`.
 *
 * Periods (length 7): [1, 2, 3, 4, 5, 6, 7] [8, 9, 10, 11, 12, 13, 14] [15, 16, 17, 18, 19, 20, 21]
 * Depending on where you put the reference date, the next period will be either 8 or 15 or later
 *
 * Example 1:
 * startDate = 2024-10-29 00:06 +0200 (Swiss time) which is 2024-10-28 22:06 +0000 (UTC)
 * period = 14 days
 * referenceDate = 2024-11-23 00:00 +0000
 * result = 2024-11-11 22:06 +0000
 *
 * Example 2:
 * startDate = 2025-01-13 23:30:00 +0000 (UTC)
 * period = 28 days
 * referenceDate = 2025-02-10 21:00:30 +0000
 * result = 2025-03-10 23:30:00 +0000 (56 days later as the reference day is at the start of the 2nd period)
 *
 * @param startDate The periods will start from this date
 * @param referenceDate The date to calculate the next period from. Defaults to now.
 * @param periodLength The length of each period in days
 * @returns The start of the next period as a Date
 */
export function calculateStartOfNextPeriod(
  startDate: Date,
  referenceDate: Date = new Date(),
  periodLength = 7,
): Date {
  // Get start of day in Zurich timezone for both dates
  const startDateStartOfDay = getStartOfDayInZurich(startDate);
  const referenceDateStartOfDay = getStartOfDayInZurich(referenceDate);

  // Calculate the diff in days between reference and start
  const diffInMs = referenceDateStartOfDay.getTime() - startDateStartOfDay.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  // Calculate the end of the last period (forward-looking)
  const endOfLastPeriod = getEndOfLastPeriod(diffInDays, periodLength);

  // Calculate the next billing date
  const nextBillingDate = new Date(startDateStartOfDay);
  nextBillingDate.setDate(nextBillingDate.getDate() + endOfLastPeriod);

  return nextBillingDate;
}

/**
 * Gets the start of day for a given date in Zurich timezone, then resets to UTC.
 */
export function getStartOfDayInZurich(date: Date): Date {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZURICH_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const zurichDateStr = formatter.format(date);

  const [year, month, day] = zurichDateStr.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
}

/**
 * Extrapolates totalUnits to the periodLength and returns the end of the last period.
 * It's forward looking so when the totalUnits are at the end of a period we get the end
 * of the next period.
 *
 * Example 1:
 * totalUnits=11, periodLength=7 => 14
 * [1, 2, 3, 4, 5, 6, 7] [8, 9, 10, 11, 12, 13, 14]
 * ^                     ^          ^           ^
 * firstPeriod           lastPeriod totalUnits  endOfLastPeriod
 *
 * Example 2:
 * totalUnits=6, periodLength=3 => 9
 * [1, 2, 3], [4, 5, 6], [7, 8, 9]
 * ^                 ^          ^
 * firstPeriod       totalUnits endOfLastPeriod
 *
 * This mirrors the backend implementation in insertion-funnel-api:
 * src/core/services/DateUtils.ts - getEndOfLastPeriod
 *
 * @param totalUnits Total units since beginning of first period
 * @param periodLength The length of the period that we will use as the conversion base
 * @returns The end of the last period
 */
function getEndOfLastPeriod(totalUnits: number, periodLength: number): number {
  // When totalUnits is a multiple of the period length, we are at the start of a new
  // period and we need to add it to the totalUnits to get the end of the last period.
  const extraPeriod = totalUnits % periodLength === 0 ? 1 : 0;

  return periodLength * (Math.ceil(totalUnits / periodLength) + extraPeriod);
}

/**
 * Converts a date to Zurich time and re-sets it to UTC while removing the time.
 *
 * Example:
 * 2021-01-01T23:30:00Z -> 2021-01-02T00:00:00Z
 */
function convertToZurichTimeAndResetAsUTC(date: Date): Date {
  return getStartOfDayInZurich(date);
}

/**
 * Calculates the next renewalPaymentDueDate based on the firstPublishedDate and the periodLength.
 *
 * It takes into account insertion status to determine if the payment is renewable.
 *
 * @param status The insertion status
 * @param firstPublishedDate The date when the insertion was first published
 * @param periodLength The billing period in days (default: 7)
 * @param referenceDate Optional reference date for testing (defaults to now)
 * @returns The next renewable payment due date. Undefined if the payment is not renewable.
 */
export function getRenewablePaymentDueDate(
  status: InsertionStatus | string | undefined,
  firstPublishedDate: string | undefined,
  periodLength = 7,
  referenceDate: Date = new Date(),
): Date | undefined {
  if (!firstPublishedDate) {
    return undefined;
  }

  const firstPublished = new Date(firstPublishedDate);
  if (Number.isNaN(firstPublished.getTime())) {
    return undefined;
  }

  const validStatuses = [InsertionStatus.Published, InsertionStatus.Archived];
  if (!validStatuses.includes(status as InsertionStatus)) {
    return undefined;
  }

  const startOfNextPeriod = calculateStartOfNextPeriod(firstPublished, referenceDate, periodLength);
  return convertToZurichTimeAndResetAsUTC(startOfNextPeriod);
}

/**
 * Calculates the renewable payment due date for an insertion.
 *
 * For T2T listings (initialPrice === 0), the Zuora subscription starts 7 days
 * after the publish date (when the free first week ends).
 * This function adjusts the calculation accordingly so the displayed date matches
 * when the billing period actually begins in Zuora.
 *
 * @param insertion The insertion object
 * @param referenceDate Optional reference date for testing (defaults to now)
 * @returns The next renewable payment due date, or undefined if not applicable
 */
export function calculateRenewablePaymentDueDate(
  insertion: Partial<Insertion> | undefined,
  referenceDate: Date = new Date(),
): Date | undefined {
  if (!insertion) {
    return undefined;
  }

  const { status, firstPublishedDate, selectedBundle } = insertion;
  const period = selectedBundle?.period ?? BundlePeriod.ONE_WEEK;

  const isFirstWeekFree = selectedBundle?.initialPrice === 0;

  let adjustedFirstPublishedDate = firstPublishedDate;
  if (isFirstWeekFree && firstPublishedDate) {
    const publishDate = new Date(firstPublishedDate);
    publishDate.setDate(publishDate.getDate() + 7);
    adjustedFirstPublishedDate = publishDate.toISOString();
  }

  return getRenewablePaymentDueDate(status, adjustedFirstPublishedDate, period, referenceDate);
}

/**
 * Determines if the resumable/reactivation date should be shown for an insertion.
 *
 * The date should be shown when:
 * 1. The insertion is ARCHIVED
 * 2. It's a multi-week bundle
 * 3. Either subscriptionResumableDate OR renewalPaymentDueDate is valid and in the future
 * 4. Not a T2T listing that's still in the free first week
 *
 * @param insertion The insertion object
 * @param referenceDate Optional reference date for testing (defaults to now)
 * @returns True if the reactivation date should be shown
 */
export function shouldShowReactivationDate(
  insertion: Partial<Insertion> | undefined,
  referenceDate: Date = new Date(),
): boolean {
  if (!insertion) {
    return false;
  }

  const { status, selectedBundle, subscriptionResumableDate, firstPublishedDate } = insertion;

  if (status !== InsertionStatus.Archived) {
    return false;
  }

  if (!isMultiWeeksInsertion(selectedBundle?.period)) {
    return false;
  }

  const isFirstWeekFree = selectedBundle?.initialPrice === 0;
  if (isFirstWeekFree && isWithinFreeWeekPeriod(firstPublishedDate, referenceDate)) {
    return false;
  }

  return !!(subscriptionResumableDate && subscriptionResumableDate > referenceDate);
}

/**
 * Gets the reactivation date to display for an insertion.
 *
 * Prefers subscriptionResumableDate (from Zuora) over calculated renewalPaymentDueDate.
 *
 * @param insertion The insertion object
 * @param referenceDate Optional reference date for testing (defaults to now)
 * @returns The reactivation date to display, or undefined if not applicable
 */
export function getReactivationDate(
  insertion: Partial<Insertion> | undefined,
  referenceDate: Date = new Date(),
): Date | undefined {
  if (!insertion) {
    return undefined;
  }

  const { subscriptionResumableDate, selectedBundle, firstPublishedDate } = insertion;

  const isFirstWeekFree = selectedBundle?.initialPrice === 0;
  const isMultiWeek = isMultiWeeksInsertion(selectedBundle?.period);
  if (isMultiWeek && isFirstWeekFree && isWithinFreeWeekPeriod(firstPublishedDate, referenceDate)) {
    return undefined;
  }

  if (subscriptionResumableDate && subscriptionResumableDate > referenceDate) {
    return subscriptionResumableDate;
  }

  return undefined;
}
